// src/Backend/SupabaseApis/selectionService.js
import { supabase } from '../../../Backend/supabaseClient'; // Ensure your client is imported correctly

/**
 * The "O" Function: Fetches data based on the current step (counter).
 * * @param {number} counter - Current depth (0=Grades, 1=Subjects, 2=Chapters)
 * @param {string|null} gradeId - Selected Grade ID (required for counter 1 & 2)
 * @param {string|null} subjectId - Selected Subject ID (required for counter 2)
 * @returns {Promise<Array>} List of items { id, title, ... }
 */
export const fetchSelectionData = async (counter, gradeId = null, subjectId = null) => {
    let data = [];
    let error = null;

    try {
        if (counter === 0) {
            // STEP 0: Fetch all Grades
            const { data: grades, error: err } = await supabase
                .from('grades')
                .select('id, name'); // Assuming column names are 'id' and 'title'
            
            if (err) throw err;
            data = grades;

        } else if (counter === 1) {
            // STEP 1: Fetch Subjects for specific Grade
            if (!gradeId) throw new Error("Grade ID is missing for step 1");

            const { data: subjects, error: err } = await supabase
                .from('subjects')
                .select('id, name')
                .eq('grade_id', gradeId); // Filtering by grade_id
            
            if (err) throw err;
            data = subjects;

        } else if (counter === 2) {
            // STEP 2: Fetch Chapters for specific Subject
            if (!subjectId) throw new Error("Subject ID is missing for step 2");

            const { data: chapters, error: err } = await supabase
                .from('chapters')
                .select('id, name')
                .eq('subject_id', subjectId); // Filtering by subject_id
            
            if (err) throw err;
            data = chapters;
        }

        return data || [];

    } catch (err) {
        console.error(`Error in fetchSelectionData (Counter: ${counter})`, err);
        return [];
    }
};


export const fetchTestTitle = async (chapterId) => {
    try {
        if (!chapterId) throw new Error("Chapter ID is required");

        // 1. Get Chapter Name & Subject ID
        const { data: chapter, error: err1 } = await supabase
            .from('chapters')
            .select('name, subject_id')
            .eq('id', chapterId)
            .single();

        
        if (err1 || !chapter) throw err1 || new Error("Chapter not found");

        // 2. Get Subject Name & Grade ID
        const { data: subject, error: err2 } = await supabase
            .from('subjects')
            .select('name, grade_id')
            .eq('id', chapter.subject_id)
            .single();

        if (err2 || !subject) throw err2 || new Error("Subject not found");

        // 3. Get Grade Name
        const { data: grade, error: err3 } = await supabase
            .from('grades')
            .select('name')
            .eq('id', subject.grade_id)
            .single();

        if (err3 || !grade) throw err3 || new Error("Grade not found");

        // --- FORMATTING LOGIC ---
        
        // Format Grade: "Grade 10" -> "G10" (Assumes format "Grade X")
        // If simply "10", it becomes "G10". 
        const rawGrade = grade.name || "";
        const gradeNum = rawGrade.replace(/\D/g, ''); // Extract numbers only
        const formattedGrade = gradeNum ? `G${gradeNum}` : rawGrade.substring(0, 3);

        // Format Subject: First 3 letters (e.g., "Mathematics" -> "Mat")
        const formattedSubject = (subject.name || "").substring(0, 3);

        // Format Chapter: Full Name
        const formattedChapter = chapter.name || "Untitled";

        // Concatenate: "G10 Mat Full Chapter Name"
        return `${formattedGrade} ${formattedSubject} ${formattedChapter}`;

    } catch (error) {
        console.error("Error constructing test title:", error);
        return "New Test"; // Fallback title
    }
};